import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import EnhancedOOHCampaignTable from '../dashboardCharts/EnhancedOOHCampaignTable';
import { dashboardApi } from '../../services/api';
import SnapshotDataService from '../../services/SnapshotDataService';
import { motion } from 'framer-motion';

const OOHTab = ({ 
  loading: parentLoading, 
  selectedSnapshotMonth, 
  snapshotData,
  isSnapshot = false,
  snapshotMonth = null,
  enhancedMetrics = null // Backend financial metrics for consistency
}) => {
  const [loading, setLoading] = useState(true);
  const [oohData, setOohData] = useState(null);
  const [year, setYear] = useState('2025');
  const [error, setError] = useState(null);

  const fetchOOHData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      
      if (selectedSnapshotMonth || isSnapshot) {
        // Snapshot mode - use SnapshotDataService
        const month = selectedSnapshotMonth || snapshotMonth;
        console.log('🏢 Fetching OOH snapshot data for:', month);
        
        // Try new snapshot structure first
        try {
          const newSnapshotResponse = await SnapshotDataService.getNewSnapshotOOHCampaigns(month);
          if (newSnapshotResponse?.data?.ooh_campaigns) {
            console.log('✅ Using new snapshot structure for OOH data');
            response = { data: newSnapshotResponse.data.ooh_campaigns };
          } else {
            // Fallback to old snapshot structure
            response = await SnapshotDataService.getOOHCampaigns(month);
          }
        } catch (newSnapshotErr) {
          console.warn('New OOH snapshot structure failed, trying old structure:', newSnapshotErr);
          try {
            response = await SnapshotDataService.getOOHCampaigns(month);
          } catch (snapshotErr) {
            console.warn('OOH snapshot endpoint failed, trying fallback:', snapshotErr);
            // Fallback to existing snapshot method
            try {
              response = await dashboardApi.getOOHCampaignDataBySnapshot(selectedSnapshotMonth);
            } catch (fallbackErr) {
              console.error('All OOH snapshot methods failed:', fallbackErr);
              response = { data: [] };
            }
          }
        }
      } else {
        // For current data, use consistent API pattern
        // Current mode - use dashboardApi
        console.log('🏢 Fetching current OOH data for year:', year);
        response = await dashboardApi.getOOHCampaignData(year);
      }
      
      // Check if we have data
      if (response.data && Array.isArray(response.data)) {
        setOohData(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Handle case where data might be nested in a 'data' property
        setOohData(response.data.data);
      } else {
        // If no data or unexpected format, use sample data
        console.warn('Unexpected data format from API, using sample data');
        setOohData([
          {
            initiative: 'Heineken',
            total_budget: 75000000000,
            longterm_planning_ytd: 8858254301,
            longterm_planning_ytg: 0,
            total_longterm_planning: 8858254301,
            given_campaign_base: {
              lcdp: 8858254301,
              led_short_term: 0,
              billboard_short_term: 0,
              street_led: 0,
              welcome_gate: 0,
              other: 0
            },
            lcdp_streetled_confirmed: 8858254301,
            total_planning_2025: 8858254301,
            total_saving: {
              longterm_unused: 66141745699,
              planning_actual_saving: 0,
              actual_saving: 0
            }
          },
          {
            initiative: 'Tiger',
            total_budget: 142810876400,
            longterm_planning_ytd: 50150348000,
            longterm_planning_ytg: 0,
            total_longterm_planning: 50150348000,
            given_campaign_base: {
              lcdp: 50150348000,
              led_short_term: 0,
              billboard_short_term: 0,
              street_led: 0,
              welcome_gate: 0,
              other: 0
            },
            lcdp_streetled_confirmed: 50150348000,
            total_planning_2025: 50150348000,
            total_saving: {
              longterm_unused: 92660528400,
              planning_actual_saving: 0,
              actual_saving: 0
            }
          },
          {
            initiative: 'Larue',
            total_budget: 26798186007,
            longterm_planning_ytd: 0,
            longterm_planning_ytg: 0,
            total_longterm_planning: 0,
            given_campaign_base: {
              lcdp: 0,
              led_short_term: 0,
              billboard_short_term: 0,
              street_led: 0,
              welcome_gate: 0,
              other: 0
            },
            lcdp_streetled_confirmed: 0,
            total_planning_2025: 0,
            total_saving: {
              longterm_unused: 26798186007,
              planning_actual_saving: 0,
              actual_saving: 0
            }
          }
        ]);
      }
    } catch (err) {
      console.error('Error fetching OOH data:', err);
      setError('Failed to load OOH campaign data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [selectedSnapshotMonth, year]);

  useEffect(() => {
    fetchOOHData();
  }, [fetchOOHData]);

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const isLoading = loading || parentLoading;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      p: { xs: 1, sm: 2 },
      boxSizing: 'border-box'
    }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' }, 
              fontWeight: 700,
              lineHeight: 1.2
            }}>OOH Campaigns {year}</Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel id="ooh-year-select-label" sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                fontWeight: 600
              }}>Year</InputLabel>
              <Select
                labelId="ooh-year-select-label"
                id="ooh-year-select"
                value={year}
                label="Year"
                onChange={handleYearChange}
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                <MenuItem value="2023" sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>2023</MenuItem>
                <MenuItem value="2024" sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>2024</MenuItem>
                <MenuItem value="2025" sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>2025</MenuItem>
              </Select>
            </FormControl>
          </Box>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <EnhancedOOHCampaignTable 
              data={oohData} 
              loading={isLoading}
              onRefresh={fetchOOHData}
              animate={true}
              sx={{ mt: 1 }}
            />
          </motion.div>
          </Box>
        )}
      </motion.div>
    </Box>
  );
};

export default OOHTab;
